import gettext from '@sdk/lang/gettext';
import { AllBreakTypes } from '@sdk/settings/types';
import { HoursWithMinutesAndSeconds } from '@sdk/utils/dateFromHours';
import { get24hTimeSlots, limitTimeSlots } from '@sdk/components/forms/TimePicker/timePicker.utils';
import { MIDNIGHT_TIME_SLOT } from '@sdk/components/forms/TimePicker/timePicker.consts';

export const getRowLabel = (breakType: AllBreakTypes) => {
  if (breakType === AllBreakTypes.standard) {
    return gettext('Break');
  }

  return breakType === AllBreakTypes.meal ? gettext('Meal break') : gettext('Rest break');
};

type PickEndBreakRangeStartProps = {
  clockIn?: HoursWithMinutesAndSeconds;
  clockOut?: HoursWithMinutesAndSeconds;
  startBreak?: HoursWithMinutesAndSeconds;
  endBreak?: HoursWithMinutesAndSeconds;
  intervalInMinutes: number;
};
/**
 * @returns returns best first slot for end break time picker.
 * For example: 10:00:00 if clockIn happened at 10:00:00 and there's no break started.
 * Another example: 10:20:00 if clockIn happened at 10:00:00 but break started at 10:20:00 (end break can't happen before start break).
 */
export const pickEndBreakRangeStart = ({
  clockIn,
  clockOut,
  startBreak,
  endBreak,
  intervalInMinutes,
}: PickEndBreakRangeStartProps) => {
  if (!startBreak && !clockIn) {
    return MIDNIGHT_TIME_SLOT;
  }

  const slots = limitTimeSlots({
    slots: get24hTimeSlots(intervalInMinutes),
    start: clockIn,
    end: clockOut,
  });

  const clockInSlotIndex = findTheClosestSlotIndex(slots, clockIn);
  const startSlotIndex = findTheClosestSlotIndex(slots, endBreak || startBreak);
  let finalSlotIndex = startSlotIndex >= clockInSlotIndex ? startSlotIndex : clockInSlotIndex;

  if (finalSlotIndex === -1) {
    // As a fallback on somehow not finding the right slot we always present full picker options.
    return finalSlotIndex;
  }

  if (endBreak) {
    // if endBreak has been provided we just want to start from this slot without further adjustments
    return slots[finalSlotIndex];
  }

  if (startBreak && finalSlotIndex === startSlotIndex && slots[startSlotIndex + 1]) {
    // We want to suggest the same slot as clockIn for the earliest available
    // but we also want to show next slot after `start` break.
    finalSlotIndex = startSlotIndex + 1;
  }

  return slots[finalSlotIndex];
};

/**
 * @param slots array of timeslots, example: ['00:00:00', '00:10:00', '00:20:00'...].
 * @param search time around which we would like to find the slot, example: '11:21:13'.
 * @returns closest slot available for given search (either earlier or exactly the same as searched value), example: '11:20:00'.
 */
const findTheClosestSlotIndex = (
  slots: HoursWithMinutesAndSeconds[],
  search: HoursWithMinutesAndSeconds
): number => {
  let slotIndex = slots.findIndex((slot) => slot === search);
  if (slotIndex === -1) {
    // Take the closest floor slot for custom start break.
    slotIndex = slots.findIndex((slot) => slot > search);
    // We found first that is greater than startBreak. If it's not the first in general, we actually need to pick one before.
    slotIndex = slotIndex <= 0 ? 0 : slotIndex - 1;
  }

  return slotIndex;
};
